import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import useLargeDataAnalyticsApi from "../../hooks/useLargeDataAnalyticsApi";

import { List, Row, Col, Divider, Space, Typography, Layout, Card } from 'antd';
import SimpleMapChart from "../../charts/MapChart";
import AntChartsBoxPlot from "../../charts/ant/AntErrorChart";
import {AbcOutlined} from "@mui/icons-material";
import AntBarChart from "../../charts/ant/AntBarChart";
import { Scatter } from '@ant-design/plots';
import { Column, Bar, Heatmap } from '@ant-design/plots';
import AxisConfig from "../../charts/ant/AxisConfig";
import {stringMixin} from "../../utils/strings";

const indexToCountryCode = {
  "^N225": "JPN",
  "^FTSE": "GBR",
  "^HSI": "CHN",
  "^STI": "SGP",
  "^NSEI": "IND",
  "^AXJO": "AUS",
  "^GSPTSE": "CAN"
};
const indexToName = {
  "^N225": "Nikkei 225",
  "^FTSE": "FTSE 100",
  "^HSI": "Hang Seng",
  "^STI": "Straits Times",
  "^NSEI": "Nifty 50",
  "^AXJO": "ASX 200",
  "^GSPTSE": "TSX Composite",
  "^VIX": "CBOE Volatility Index",
  "^VVIX": "CBOE VVIX Index",
  "^VIX9D": "CBOE 9-Day Volatility Index",
  "^SKEW": "CBOE SKEW Index",
    "^VXN": "CBOE Nasdaq Volatility Index"
};

const VolatilityFactors = [
    {k: 'VIX', v: 'CBOE Volatility Index'},
    {k: 'VVIX', v: 'CBOE VVIX Index'},
    {k: 'SKEW', v: 'CBOE SKEW Index'},
    {k: 'VXN', v: 'CBOE Nasdaq Volatility Index'}
];

const CountryFactors = [
    {k: 'N225', v: 'Nikkei 225'},
    {k: 'FTSE', v: 'FTSE 100'},
    {k: 'HSI', v: 'Hang Seng'},
    {k: 'STI', v: 'Straits Times'},
    {k: 'NSEI', v: 'Nifty 50'},
    {k: 'AXJO', v: 'ASX 200'},
    {k: 'GSPTSE', v: 'TSX Composite'}
]

const parseFeatureImportance = (o)=>{
    const array = Object.entries(o).map(([k, value]) => ({ k, value }));
    array.sort((a, b) => a.value - b.value);
    return array.map(({k, value})=>({id: k, [k]: (value * 100).toFixed(2)}));
}



function convertFeaturesToBoxPlot(featureImportance, featureImportanceCI) {
  return Object.keys(featureImportance).map(feature => {
    const avg = featureImportance[feature];
    const { lower, upper } = featureImportanceCI[feature];

    // Calculate IQR (Inter-Quartile Range) as 50% of the range between lower and upper
    const iqr = (upper - lower) * 0.5;
    const q1 = avg - iqr / 2;
    const q3 = avg + iqr / 2;
    const values = [lower * 0.8, q1 * 0.8, avg, q3 * 1.2, upper * 1.2].map(x=>parseFloat((x*100).toFixed(2)));
    return {
      x: indexToName[feature.toUpperCase()],
        y: values,

    };
  });
}


const FeatureDotGrid = ({modelDescription})=>{
    const data = useMemo(() => {
        return modelDescription.flatMap(item =>
          Object.entries(item.coefficients).map(([feature, coefficient]) => ({
            target: item.target,
            feature: feature.slice(1),  // Remove the '^' prefix
            value: coefficient * 100,
            low: item.ci_lower[feature],
            high: item.ci_upper[feature],
            intercept: item.intercept
          }))
        );
      }, []);

    console.log(data)

      const config = {
          data,
          xField: 'feature',
          yField: 'target',
          colorField: 'value',
          sizeField: 'value',

          label: {
              text: (d) => d.value.toFixed(1),
              position: 'inside',
              style: {
                  fill: '#fff',
                  pointerEvents: 'none',
              },
          },
          mark: 'cell',
            scale: {
              color: {
                palette: 'rdBu',
                offset: (t) => 1 - t,
              },
                size: [20]
            },
          legend: {}
      }
      return <Heatmap {...config} />;
}

const FeatureColumn = ({data})=>{
    console.log(data);
    const config = {
        data,
        xField: 'id',
        yField: 'value',
        sort: {
            reverse: true
        },
        style: {
            radiusTopLeft: 4,
            radiusTopRight: 4,
            fill: 'linear-gradient(to right, #57a5fc, #0072f3)',
        },
        barStyle: {
            radius: [20, 20, 20, 20],
        },        axis: {
            x: new AxisConfig({}).getConfig()
        },
    }

    return <Bar {...config} />
}


const AttributionResults = ({ results }) => {
    const countryImportance = parseFeatureImportance(results?.country_results?.feature_importance);
    const volatilityImportance = parseFeatureImportance(results?.vol_results?.feature_importance);
    const countryR2 = results?.country_results?.r_squared || 0;
    const volatilityR2 = results?.vol_results?.r_squared || 0;

    const codedCountryImportance = countryImportance.map(({id, ...rest})=>({ id: indexToCountryCode[id.toUpperCase()], value: rest[id]}));
    const labeledCountryImportance = countryImportance.map(({id, ...rest})=>({ id: indexToName[id.toUpperCase()], [indexToName[id.toUpperCase()]]: rest[id]}));
    const labeledVolatilityImportance = volatilityImportance.map(({id, ...rest})=>({
        id: indexToName[id.toUpperCase()], value: parseFloat(rest[id])
    }));

    const countryBoxPlot = convertFeaturesToBoxPlot(results?.country_results?.feature_importance, results?.country_results?.feature_importance_ci);

    console.log(countryBoxPlot)
    const commonChart = {
        vertical: false,
        mode: 'stacked',
        showLegend: false,
        paddingLeft: 200,
        labelFontSize: 12,
        borderRadius: 4
    }
    return (
        <Layout>
            <Row gutter={64} justify={'center'} className={'mt-6'}>
                <Col flex={'0 0 600px'}>
                    <Row justify={'space-between'}>
                        <Col span={12}>
                            <Space direction={'vertical'}>
                                <Typography.Title level={4}>Volatility Impact on Holdings</Typography.Title>
                                <Typography.Text strong>
                                    {stringMixin.pct(volatilityR2)}  Explained by Factor Group
                                </Typography.Text>
                            </Space>
                        </Col>
                        <Col span={12}>
                            <List
                                grid={{gutter: 4, column: 1}}
                                dataSource={VolatilityFactors}
                                renderItem={(item) => (
                                   <List.Item key={item.k}>
                                       <b style={{color: "#0033ff"}}>{item.k}</b>  <Typography.Text>{item.v}</Typography.Text>
                                    </List.Item>
                                )}
                            />
                        </Col>

                    </Row>
                    <Divider/>
                    <div style={{height: 300, width: 600}}>
                        <FeatureColumn data={labeledVolatilityImportance} />
                    </div>
                    <FeatureDotGrid modelDescription={results.vol_results?.model_description} />
                </Col>
                <Col flex={'0 0 800px'}>
                    <Row wrap={false} justify={'space-between'}>
                        <Col span={12}>
                            <Space direction={'vertical'}>
                                <Typography.Title level={4}>Country Impact on Holdings</Typography.Title>
                                <Typography.Text strong>
                                    {stringMixin.pct(countryR2)}  Explained by Factor Group
                                </Typography.Text>
                            </Space>
                        </Col>
                        <Col>
                            <List
                                grid={{gutter: 4, column: 2}}
                                dataSource={CountryFactors}
                                renderItem={(item) => (
                                   <List.Item key={item.k}>
                                       <b style={{color: "#0033ff"}}>{item.k}</b>  <Typography.Text>{item.v}</Typography.Text>
                                    </List.Item>
                                )}
                            />
                        </Col>
                    </Row>
                    <Divider/>
                    <div style={{height: 300, width: 800}}>
                        <AntChartsBoxPlot data={countryBoxPlot} />
                    </div>
                    <div style={{height: 600, width: 800}}>
                        <SimpleMapChart data={codedCountryImportance}  />
                    </div>
                </Col>
            </Row>
        </Layout>
    )

}

const AttributionModel = ({ tickers }) => {
    const [searchParams] = useSearchParams();

    // If tickers prop is provided, use it; otherwise, parse from query params
    const tickersFromQuery = searchParams.get('tickers');
    const tickersList = tickers ? tickers : tickersFromQuery ? tickersFromQuery.split(',') : [];

    const [currentTickers, setCurrentTickers] = useState(tickersList);

    const { attributionAnalysis, performAttributionAnalysis } = useLargeDataAnalyticsApi();

    useEffect(() => {
        if (currentTickers.length > 0) {
            performAttributionAnalysis(currentTickers);
        }
    }, [currentTickers]);

    console.log(attributionAnalysis);



    return (
        <div>


            {attributionAnalysis?.length > 0 && <AttributionResults results={attributionAnalysis[0]} />}
        </div>
    );
};

export default AttributionModel;
